import React, { useMemo, useState } from "react";
import { AlertCircle, BarChart3, Download, FileUp, Lightbulb, Loader2, ReceiptText, ShieldCheck, Trash2, UserRoundCheck } from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import socioService from "../services/socioService.tsx";
import energiaAsistenciaService, { type AnalisisFactura } from "../services/energiaAsistenciaService.ts";

const money = (n: number) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n || 0);

const riskColor: Record<AnalisisFactura["riesgo"], string> = {
  bajo: "bg-emerald-100 text-emerald-700",
  medio: "bg-amber-100 text-amber-700",
  alto: "bg-rose-100 text-rose-700",
};

const flattenObjectToRows = (value: any, prefix = ""): Array<{ campo: string; valor: string }> => {
  if (value === null || value === undefined) {
    return [{ campo: prefix || "valor", valor: "—" }];
  }

  if (typeof value !== "object") {
    return [{ campo: prefix || "valor", valor: String(value) }];
  }

  if (Array.isArray(value)) {
    if (!value.length) return [{ campo: prefix || "array", valor: "[]" }];
    return value.flatMap((item, idx) => flattenObjectToRows(item, `${prefix}[${idx}]`));
  }

  return Object.entries(value).flatMap(([k, v]) =>
    flattenObjectToRows(v, prefix ? `${prefix}.${k}` : k)
  );
};

const AsistenciaEnergia: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [analisis, setAnalisis] = useState<AnalisisFactura | null>(null);
  const [historial, setHistorial] = useState<AnalisisFactura[]>([]);
  const [error, setError] = useState<string>("");
  const [detalleOpen, setDetalleOpen] = useState<AnalisisFactura | null>(null);
  const [deleteOpen, setDeleteOpen] = useState<AnalisisFactura | null>(null);

  const socio = socioService.getSelectedSocioFromLocalStorage();
  const socioId = (socio as any)?.socio_id || (socio as any)?.id;

  const loadHistorial = async () => {
    if (!socioId) return;
    const data = await energiaAsistenciaService.obtenerHistorial(socioId, getAccessTokenSilently);
    setHistorial(data);
  };

  React.useEffect(() => {
    loadHistorial();
  }, []);

  const handleDescargar = (h: AnalisisFactura) => {
    const href = h.archivoDataUrl || h.archivoUrl;
    if (!href) {
      setError("Este registro no tiene archivo disponible para descarga.");
      return;
    }

    const link = document.createElement("a");
    link.href = href;
    link.download = h.archivoNombre || `factura-${h.facturaId}`;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEliminar = (h: AnalisisFactura) => {
    setDeleteOpen(h);
  };

  const confirmarEliminar = async () => {
    if (!socioId || !deleteOpen) return;
    try {
      await energiaAsistenciaService.eliminarFactura(socioId, deleteOpen.facturaId, getAccessTokenSilently);
      setDeleteOpen(null);
      await loadHistorial();
    } catch {
      setError("No se pudo eliminar la factura.");
    }
  };

  const handleDetalle = (h: AnalisisFactura) => {
    setDetalleOpen(h);
  };

  const handleAnalizar = async () => {
    if (!file) {
      setError("Subí una factura para analizar.");
      return;
    }
    if (!socioId) {
      setError("No hay socio seleccionado. Cambiá de socio y volvé a intentar.");
      return;
    }

    try {
      setError("");
      setLoading(true);
      const result = await energiaAsistenciaService.analizarFactura(file, socioId, getAccessTokenSilently);
      setAnalisis(result);
      await loadHistorial();
    } catch (e) {
      setError("No se pudo analizar la factura en este momento.");
    } finally {
      setLoading(false);
    }
  };

  const resumen = useMemo(() => {
    if (!historial.length) return null;
    const promedio = Math.round(historial.reduce((acc, r) => acc + r.consumoKwh, 0) / historial.length);
    const ultimo = historial[0]?.consumoKwh || 0;
    const variacion = promedio ? Math.round(((ultimo - promedio) / promedio) * 100) : 0;
    return { promedio, ultimo, variacion };
  }, [historial]);

  const detalleRows = useMemo(() => {
    if (!detalleOpen) return [];
    return flattenObjectToRows(
      {
        facturaId: detalleOpen.facturaId,
        titular: detalleOpen.titular,
        direccion: detalleOpen.direccion,
        periodo: detalleOpen.periodo,
        vencimiento: detalleOpen.vencimiento,
        consumoKwh: detalleOpen.consumoKwh,
        importeTotal: detalleOpen.importeTotal,
        energiaActiva: detalleOpen.energiaActiva,
        cargosFijos: detalleOpen.cargosFijos,
        impuestos: detalleOpen.impuestos,
        riesgo: detalleOpen.riesgo,
        recomendaciones: detalleOpen.recomendaciones,
        detalleIA: detalleOpen.detalle,
      },
      ""
    );
  }, [detalleOpen]);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <section className="rounded-2xl bg-gradient-to-r from-sky-700 to-blue-700 p-6 text-white shadow-lg">
          <p className="text-xs font-semibold uppercase tracking-wider text-sky-100">Asistencia energética para socios</p>
          <h1 className="mt-1 text-2xl font-extrabold sm:text-3xl">Asistencia de Consumos de Energía</h1>
          <p className="mt-2 max-w-3xl text-sm text-sky-100">
            Subí una factura, detectamos automáticamente los valores con AI/OCR y te devolvemos un diagnóstico para
            reclamos o para coordinar asistencia técnica con ingeniería energética.
          </p>
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800">
              <FileUp className="h-5 w-5 text-sky-600" /> Cargar factura
            </h2>
            <div className="mt-4 space-y-3">
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full rounded-lg border border-slate-200 p-3 text-sm"
              />
              {file && <p className="text-xs text-slate-500">Archivo: {file.name}</p>}
              <button
                onClick={handleAnalizar}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ReceiptText className="h-4 w-4" />}
                {loading ? "Analizando factura..." : "Analizar factura"}
              </button>
              {error && (
                <p className="flex items-center gap-2 rounded-lg bg-rose-50 p-2 text-xs text-rose-700">
                  <AlertCircle className="h-4 w-4" /> {error}
                </p>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800">
              <BarChart3 className="h-5 w-5 text-sky-600" /> Resumen socio
            </h2>
            {!resumen ? (
              <p className="mt-4 text-sm text-slate-600">Todavía no hay facturas analizadas para este socio.</p>
            ) : (
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Último consumo</p>
                  <p className="text-xl font-bold text-slate-800">{resumen.ultimo} kWh</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Promedio</p>
                  <p className="text-xl font-bold text-slate-800">{resumen.promedio} kWh</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Variación</p>
                  <p className={`text-xl font-bold ${resumen.variacion > 15 ? "text-rose-600" : "text-emerald-600"}`}>
                    {resumen.variacion > 0 ? "+" : ""}{resumen.variacion}%
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {analisis && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-bold text-slate-800">Feedback de factura detectada</h2>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase ${riskColor[analisis.riesgo]}`}>
                Riesgo {analisis.riesgo}
              </span>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-xl bg-slate-50 p-3"><p className="text-xs text-slate-500">Proveedor</p><p className="font-semibold">{analisis.proveedor}</p></div>
              <div className="rounded-xl bg-slate-50 p-3"><p className="text-xs text-slate-500">Período</p><p className="font-semibold">{analisis.periodo}</p></div>
              <div className="rounded-xl bg-slate-50 p-3"><p className="text-xs text-slate-500">Consumo</p><p className="font-semibold">{analisis.consumoKwh} kWh</p></div>
              <div className="rounded-xl bg-slate-50 p-3"><p className="text-xs text-slate-500">Total factura</p><p className="font-semibold">{money(analisis.importeTotal)}</p></div>
              <div className="rounded-xl bg-slate-50 p-3"><p className="text-xs text-slate-500">Titular</p><p className="font-semibold">{analisis.titular || "No detectado"}</p></div>
              <div className="rounded-xl bg-slate-50 p-3"><p className="text-xs text-slate-500">Dirección</p><p className="font-semibold">{analisis.direccion || "No detectada"}</p></div>
            </div>

            <div className="mt-4 rounded-xl bg-blue-50 p-4">
              <h3 className="flex items-center gap-2 text-sm font-bold text-blue-800">
                <Lightbulb className="h-4 w-4" /> Recomendaciones automáticas
              </h3>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {analisis.recomendaciones.map((r) => (
                  <li key={r}>• {r}</li>
                ))}
              </ul>
              <div className="mt-3 flex flex-wrap gap-2">
                <button className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700">
                  <ShieldCheck className="h-4 w-4" /> Generar pre-reclamo
                </button>
                <button className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50">
                  <UserRoundCheck className="h-4 w-4" /> Solicitar asistencia de ingeniería
                </button>
              </div>
            </div>
          </section>
        )}

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-bold text-slate-800">Base de facturas del socio</h2>
          {historial.length === 0 ? (
            <p className="mt-3 text-sm text-slate-600">Sin registros aún. Al analizar la primera factura se crea la base del socio.</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[920px] text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2">Fecha</th>
                    <th className="py-2">Titular</th>
                    <th className="py-2">Dirección</th>
                    <th className="py-2">Período</th>
                    <th className="py-2">Consumo</th>
                    <th className="py-2">Total</th>
                    <th className="py-2">Riesgo</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.map((h) => (
                    <tr key={h.facturaId} className="border-t border-slate-100">
                      <td className="py-2">{new Date(h.createdAt).toLocaleDateString("es-AR")}</td>
                      <td className="py-2">{h.titular || "—"}</td>
                      <td className="py-2 max-w-[260px] truncate" title={h.direccion || ""}>{h.direccion || "—"}</td>
                      <td className="py-2">{h.periodo}</td>
                      <td className="py-2">{h.consumoKwh} kWh</td>
                      <td className="py-2">{money(h.importeTotal)}</td>
                      <td className="py-2">
                        <span className={`rounded-full px-2 py-1 text-[11px] font-semibold uppercase ${riskColor[h.riesgo]}`}>
                          {h.riesgo}
                        </span>
                      </td>
                      <td className="py-2">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleDetalle(h)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Detalle
                          </button>
                          <button
                            onClick={() => handleDescargar(h)}
                            className="inline-flex items-center gap-1 rounded-lg border border-sky-200 bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-100"
                          >
                            <Download className="h-3.5 w-3.5" /> Descargar
                          </button>
                          <button
                            onClick={() => handleEliminar(h)}
                            className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {detalleOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-[1000px] max-w-[1000px] rounded-xl bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Detalle completo de análisis</h3>
              <button className="rounded border px-3 py-1" onClick={() => setDetalleOpen(null)}>Cerrar</button>
            </div>
            <div className="max-h-[70vh] overflow-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-100">
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-600">
                    <th className="px-3 py-2 w-[45%]">Campo</th>
                    <th className="px-3 py-2">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {detalleRows.map((row, idx) => (
                    <tr key={`${row.campo}-${idx}`} className="border-t border-slate-100 align-top">
                      <td className="px-3 py-2 font-mono text-xs text-slate-600 break-all">{row.campo}</td>
                      <td className="px-3 py-2 text-slate-900 break-all">{row.valor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {deleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">Eliminar factura</h3>
            <p className="mt-2 text-sm text-slate-600">
              ¿Seguro que querés eliminar esta factura del historial?
            </p>
            <div className="mt-4 rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
              <p><b>Fecha:</b> {new Date(deleteOpen.createdAt).toLocaleDateString("es-AR")}</p>
              <p><b>Titular:</b> {deleteOpen.titular || "—"}</p>
              <p><b>Total:</b> {money(deleteOpen.importeTotal)}</p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="rounded border px-3 py-2 text-sm" onClick={() => setDeleteOpen(null)}>Cancelar</button>
              <button className="rounded bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700" onClick={confirmarEliminar}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AsistenciaEnergia;
